import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import browserHistory from '../../../browserHistory';
import { Loader } from '../../App/components/Loader';
import { getCurrentClassroomId } from '../selectors/users';
import { getClassroom } from '../../../firebase/classrooms';
import { listProjects } from '../../../firebase/projects';
import { closeClassroomModal } from '../actions/ide';

/**
 * Browse the current classroom's students → sketches (staff only). This is the mobile / non-hover
 * counterpart of the desktop <ClassroomStudentsMenu/> flyout: pick a student → see their sketches
 * (including Private ones, permitted by the staff read rule) → open one to help. Opening navigates
 * the editor to the sketch and closes the modal.
 */
const BrowseClassroomModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentClassroomId = useSelector(getCurrentClassroomId);

  const [classroom, setClassroom] = useState(null); // null = loading
  const [selectedStudentUid, setSelectedStudentUid] = useState('');
  const [studentProjects, setStudentProjects] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    if (!currentClassroomId) {
      setClassroom(false); // false = resolved, none
      return () => {};
    }
    getClassroom(currentClassroomId)
      .then((room) => {
        if (!cancelled) setClassroom(room || false);
      })
      .catch(() => {
        if (!cancelled) setClassroom(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentClassroomId]);

  function handleSelectStudent(e) {
    const uid = e.target.value;
    setSelectedStudentUid(uid);
    setStudentProjects(null);
    if (!uid || !classroom) return;
    const student = classroom.students.find((s) => s.uid === uid);
    if (!student) return;
    // Scoped to the current classroom, matching the desktop <ClassroomStudentsMenu/> flyout.
    listProjects(student.username, true, { classroomId: currentClassroomId })
      .then((res) => setStudentProjects(res.projects))
      .catch(() => setStudentProjects([]));
  }

  function openProject(projectId) {
    const student = classroom.students.find(
      (s) => s.uid === selectedStudentUid
    );
    if (!student) return;
    dispatch(closeClassroomModal());
    // state.confirmed:true bypasses IDEView's unsaved-changes <Prompt> (same as newProject()),
    // which would otherwise silently block jumping from the editor to another sketch.
    browserHistory.push(`/${student.username}/sketches/${projectId}`, {
      confirmed: true
    });
  }

  if (classroom === null) {
    return (
      <div className="classroom-modal" style={{ minHeight: '6rem' }}>
        <Loader />
      </div>
    );
  }

  if (!classroom) {
    return (
      <div className="classroom-modal">
        <p>
          {t('ClassroomMenu.NoCurrentClassroom', {
            defaultValue: 'You are not in a classroom yet.'
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="classroom-modal">
      <p className="classroom-modal__name">{classroom.name}</p>

      <label htmlFor="student-select">
        {t('ViewClassroomModal.StudentLabel', { defaultValue: 'Students' })}
      </label>
      <select
        id="student-select"
        value={selectedStudentUid}
        onChange={handleSelectStudent}
      >
        <option value="">
          {classroom.students.length === 0
            ? t('ClassroomMenu.NoStudents', {
                defaultValue: 'No students have joined yet'
              })
            : t('ViewClassroomModal.SelectStudent', {
                defaultValue: 'Select a student…'
              })}
        </option>
        {classroom.students.map((s) => (
          <option key={s.uid} value={s.uid}>
            {s.username}
          </option>
        ))}
      </select>

      {selectedStudentUid && studentProjects === null && (
        <div style={{ minHeight: '4rem' }}>
          <Loader />
        </div>
      )}
      {selectedStudentUid && studentProjects !== null && (
        <ul className="classroom-modal__projects">
          {studentProjects.length === 0 ? (
            <li>
              {t('ClassroomMenu.NoSketches', {
                defaultValue: 'This student has no sketches yet.'
              })}
            </li>
          ) : (
            studentProjects.map((p) => (
              <li key={p.id}>
                <button type="button" onClick={() => openProject(p.id)}>
                  {p.name}
                  {p.visibility === 'Private'
                    ? ` (${t('ViewClassroomModal.Private', {
                        defaultValue: 'Private'
                      })})`
                    : ''}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default BrowseClassroomModal;
